import React, { useState, useEffect, useRef, FC, Dispatch, SetStateAction } from 'react'
import { getGoogleCookieUserId } from 'lib/cookies'
import { useSelector } from 'react-redux'
import { getIndice } from 'selectors/algolia'
import { getArtworkCategory, getArtworkId } from 'apps/saatchi/artdetail2/selectors/artwork'
import { getRelatedSeeMore } from 'apps/saatchi/artdetail2/selectors/carousel'
import { getUserID } from 'selectors/user/session'
import { getAlgoliaUserToken } from 'selectors/user/cookie'
import CarouselV2 from 'components/SA/Carousel/v2'
import useCarouselImageLazyLoading from 'components/SA/Carousel/v2/useCarouselImageLazyLoading'
import {
  getGoogleArtworksPDP,
  clickArtworkCarousel,
  GOOGLE,
  getPluralizedCategory,
} from '../helpers'
import Polaroid from '../ProductCarouselPolaroid'
import SeeMoreTile from '../SeeMoreTile'
import styles from '../ProductCarouselPolaroid.module.scss'

const MIN_ARTWORKS = 5
const LIST_ID = 'you-may-also-like'
const DATA_API = GOOGLE

type RecommendedArtworksCarouselProps = {
  shouldLoadComponent: boolean
  setShouldLoadComponent: Dispatch<SetStateAction<boolean>>
}

const RecommendedArtworksCarousel: FC<RecommendedArtworksCarouselProps> = (props) => {
  const { shouldLoadComponent, setShouldLoadComponent } = props
  /* Replacing connector */
  const algoliaSearchIndex = useSelector(getIndice)
  const selectedCategory = useSelector(getArtworkCategory)
  const userId = useSelector(getUserID)
  const artworkId = useSelector(getArtworkId)
  const algoliaUserToken = useSelector(getAlgoliaUserToken)
  const seeMore = useSelector(getRelatedSeeMore)
  /* ============================================================== */
  const [artworks, handleArtworks] = useState([])
  const polaroidRef = useRef<HTMLDivElement>(null)
  const [polaroidWidth, setPolaroidWidth] = useState<number>(
    polaroidRef?.current?.offsetWidth || 270
  )
  const { handleImageLazyLoading, lastImageIndexLoaded } = useCarouselImageLazyLoading({
    offset: 5,
  })
  const trimmedCategory = selectedCategory.replace(/s$/, '').replace(/\sM/, 'm')
  const filter = trimmedCategory.length
    ? `tag="${trimmedCategory || 'Painting'}" filterOutOfStockItems`
    : ''
  const { link, title } = seeMore
  const LIST_NAME = `${getPluralizedCategory(selectedCategory)} You May Also Like`
  useEffect(() => {
    if (shouldLoadComponent) {
      const googleUserID = getGoogleCookieUserId()
      getGoogleArtworksPDP({
        artworkId,
        callBack: handleArtworks,
        category: selectedCategory,
        visitorId: googleUserID,
        filter,
      })
    }
  }, [artworkId, shouldLoadComponent])

  useEffect(() => {
    if (!!polaroidRef?.current?.offsetWidth) {
      setPolaroidWidth(polaroidRef?.current?.offsetWidth)
    }
  }, [polaroidRef?.current?.offsetWidth])

  const handleClick = (ev: React.SyntheticEvent<Record<string, any>>) => {
    setShouldLoadComponent(false)
    clickArtworkCarousel(ev, algoliaSearchIndex, algoliaUserToken, userId)
  }

  if (!artworks || artworks.length < MIN_ARTWORKS) {
    return null
  }

  return (
    <div data-section={LIST_ID}>
      <CarouselV2
        title={LIST_NAME}
        polaroidWidth={polaroidWidth}
        customStyle={styles.carouselPolaroidImage}
        onScrollCallback={handleImageLazyLoading}
      >
        {artworks.map((artwork, index) => (
          <Polaroid
            key={index}
            polaroidRef={polaroidRef}
            artwork={artwork}
            onImageClick={handleClick}
            onTitleClick={handleClick}
            index={index}
            listId={LIST_ID}
            listName={LIST_NAME}
            dataApi={DATA_API}
            isLazyLoading={index > lastImageIndexLoaded}
          />
        ))}
        <SeeMoreTile link={link} title={title} />
      </CarouselV2>
    </div>
  )
}

export default RecommendedArtworksCarousel
